import React, { useEffect, useState } from "react";
import { TrendingUp, TrendingDown, Loader2 } from "lucide-react";
import api, { formatMoney } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { useHistoryBack } from "../lib/useHistoryBack";
import { VolverButton } from "./VolverButton";

const pct = (v) => (v == null ? "—" : `${v.toLocaleString("es-ES", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`);

function Parte({ label, margen, p, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
      <div className="text-sm font-bold font-mono">{formatMoney(margen)}</div>
      <div className="text-xs text-slate-500">{pct(p)} margen</div>
    </div>
  );
}

function FamiliasDialog({ open, onOpenChange, data }) {
  const fams = data?.familias_menos_rentables || [];
  const nav = useHistoryBack(open, () => onOpenChange(false), { escape: false });
  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : nav.close())}>
      <DialogContent className="max-w-none w-screen h-screen sm:rounded-none overflow-auto bg-white" data-testid="familias-menos-rentables-dialog">
        <DialogHeader className="flex-row items-start gap-4 space-y-0 text-left">
          <VolverButton onClick={nav.back} testid="familias-volver" />
          <div>
          <DialogTitle className="font-display">Familias menos rentables · {data?.label}</DialogTitle>
          <DialogDescription>
            Las 5 familias con menor % de margen del mes (solo familias con más de {formatMoney(data?.minimo_facturacion)} facturados).
          </DialogDescription>
          </div>
        </DialogHeader>
        {fams.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="familias-vacio">No hay familias con coste conocido este mes.</p>
        ) : (
          <ol className="space-y-2">
            {fams.map((f, i) => (
              <li key={f.familia} className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2" data-testid={`familia-rentable-${i}`}>
                <span className="h-7 w-7 shrink-0 rounded-full bg-red-50 text-red-700 text-xs font-bold flex items-center justify-center">{i + 1}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate">{f.familia}</div>
                  <div className="text-xs text-slate-500">{formatMoney(f.facturacion)} facturados · {f.productos} productos</div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-bold text-red-600">{pct(f.pct)}</div>
                  <div className="text-[11px] text-slate-500 font-mono">{formatMoney(f.margen)}</div>
                </div>
              </li>
            ))}
          </ol>
        )}
      </DialogContent>
    </Dialog>
  );
}

export const MargenCard = ({ importId, margen, hist }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!importId || margen == null) { setData(null); return; }
    setData(null);
    setLoading(true);
    api.get(`/dashboard/margen/${importId}`).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [importId, margen]);

  return (
    <div className="card p-5" data-testid="kpi-margen">
      <div className="flex items-start justify-between">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Margen bruto estimado</div>
        <TrendingUp size={16} className="text-slate-400" />
      </div>
      <div className="font-display text-2xl font-extrabold mt-1" data-testid="margen-valor">{margen == null ? "—" : formatMoney(margen)}</div>
      {margen == null ? (
        <div className="text-xs text-slate-500 mt-0.5">sin estudio de stock de referencia</div>
      ) : (
        <>
          <div className="text-xs text-slate-500 mt-0.5" data-testid="margen-pct">
            {loading ? <Loader2 size={12} className="inline animate-spin" /> : <><b className="text-slate-800">{pct(data?.margen_pct)}</b> sobre la facturación</>}
          </div>
          {hist && <div className="mt-1.5 pt-1.5 border-t border-slate-100">{hist}</div>}
          {data && (
            <div className="grid grid-cols-2 gap-2 mt-2 pt-2 border-t border-slate-100">
              <Parte label="Farmacia" margen={data.margen_farmacia} p={data.margen_pct_farmacia} testid="margen-farmacia" />
              <Parte label="Parafarmacia" margen={data.margen_parafarmacia} p={data.margen_pct_parafarmacia} testid="margen-parafarmacia" />
            </div>
          )}
          <button type="button" onClick={() => setOpen(true)} disabled={!data}
                  className="mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-full border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-700 transition-colors hover:bg-red-100 disabled:opacity-50"
                  data-testid="ver-familias-menos-rentables-btn">
            <TrendingDown size={13} /> ¿Quieres ver las familias menos rentables?
          </button>
          <FamiliasDialog open={open} onOpenChange={setOpen} data={data} />
        </>
      )}
    </div>
  );
};
